import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Upload, Play, Check, Loader2, AlertCircle, X, Trophy } from 'lucide-react';
import { socialAPI, playOfWeekAPI, BACKEND_URL } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Switch } from '../components/ui/switch';

const CreatePost = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState('video'); // 'video' | 'image'
  const [selectedImage, setSelectedImage] = useState(null); // { url, file }

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [caption, setCaption] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isEventEntry, setIsEventEntry] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);



  const [selectedVideoData, setSelectedVideoData] = useState(null); // { id, url, title }

  // Initial load - fetch active event
  useEffect(() => {
    const fetchActiveEvent = async () => {
      try {
        const response = await playOfWeekAPI.getCurrent();
        console.log('DEBUG: Active event response:', response.data);
        if (response.data && response.data.id && response.data.status === 'active') {
          setActiveEvent(response.data);
        }
      } catch (error) {
        console.error('Error fetching active event:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchActiveEvent();
  }, []);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation based on mode
    if (mode === 'video') {
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg'];
      if (!allowedTypes.includes(file.type)) {
        toast({ title: t('post.invalidType'), description: 'Allowed: MP4, MOV, AVI, WebM', variant: 'destructive' });
        return;
      }
      if (file.size > 200 * 1024 * 1024) {
        toast({ title: t('post.fileTooLarge'), description: 'Max 200MB', variant: 'destructive' });
        return;
      }
    } else {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast({ title: t('post.invalidType'), description: 'Allowed: JPG, PNG, WebP, GIF', variant: 'destructive' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: t('post.fileTooLarge'), description: 'Max 10MB', variant: 'destructive' });
        return;
      }
    }

    try {
      setUploading(true);
      setUploadProgress(10);

      if (mode === 'video') {
        const response = await socialAPI.uploadVideo(file);
        setUploadProgress(100);
        const newVideo = {
          id: response.data.video_id,
          video_url: response.data.video_url,
          title: file.name.replace(/\.[^/.]+$/, ''),
          created_at: new Date().toISOString()
        };
        setSelectedVideoData(newVideo);
        setSelectedVideoId(response.data.video_id);
        toast({ title: t('post.videoUploaded', 'Video uploaded!') });
      } else {
        // Image Upload
        const formData = new FormData();
        formData.append('file', file);
        const response = await socialAPI.uploadImage(formData);
        setUploadProgress(100);
        setSelectedImage({ url: response.data.image_url, file });
        toast({ title: "Image uploaded!" });
      }

    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      toast({
        title: t('post.uploadFailed', 'Upload failed'),
        description: detail,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'video' && !selectedVideoId) {
      toast({ title: t('post.selectVideo', 'Please select a video'), variant: 'destructive' });
      return;
    }
    if (mode === 'image' && !selectedImage) {
      toast({ title: "Please upload an image", variant: 'destructive' });
      return;
    }

    try {
      setSubmitting(true);
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);

      const payload = {
        caption: caption || null,
        tags,
        is_event_entry: mode === 'video' ? isEventEntry : false,
        event_id: (mode === 'video' && isEventEntry) ? activeEvent?.id : null
      };

      if (mode === 'video') {
        payload.video_id = selectedVideoId;
      } else {
        payload.image_url = selectedImage.url;
      }

      await socialAPI.createPost(payload);
      toast({ title: t('post.created', 'Post created!') });
      navigate('/feed');
    } catch (error) {
      toast({ title: error.response?.data?.detail || t('common.error'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-gray-400">
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('common.back', 'Back')}
      </Button>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white mb-6">Create Post</h1>
        <div className="flex bg-[#1a1a1b] p-1 rounded-lg border border-gray-800">
          <button
            onClick={() => setMode('video')}
            className={`px-4 py-1.5 text-sm rounded-md transition ${mode === 'video' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Video
          </button>
          <button
            onClick={() => setMode('image')}
            className={`px-4 py-1.5 text-sm rounded-md transition ${mode === 'image' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Photo
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Media Upload Section */}
        <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              {mode === 'video' ? t('post.selectVideo', 'Select Video') : "Upload Photo"}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload {mode === 'video' ? 'Video' : 'Photo'}</>
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={mode === 'video' ? "video/mp4,video/quicktime,video/x-msvideo,video/webm,video/mpeg" : "image/jpeg,image/png,image/webp,image/gif"}
                onChange={handleFileSelect}
                className="hidden"
              />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Upload Info */}
            <div className="mb-4 p-3 bg-[#2a2a2b] rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-gray-400 text-sm">
                {mode === 'video'
                  ? t('post.uploadInfo', 'Upload a video file (MP4, MOV, AVI, WebM). Max 200MB, max 2 minutes.')
                  : "Upload a photo (JPG, PNG, WebP). Max 10MB."}
              </p>
            </div>

            {uploading && (
              <div className="mb-4">
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {mode === 'video' ? (
              loading ? (
                <p className="text-gray-400">{t('common.loading')}</p>
              ) : !selectedVideoData ? (
                <div className="w-full h-40 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-500">
                  {t('post.noVideos', 'Upload a video to post')}
                </div>
              ) : (
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden border-2 border-blue-500">
                  {selectedVideoData.video_url && (
                    <video
                      src={(() => {
                        const rawUrl = selectedVideoData.video_url;
                        const urlWithToken = `${rawUrl}${rawUrl?.includes('/stream') ? `?token=${localStorage.getItem('token')}` : ''}`;
                        if (urlWithToken.startsWith('http')) return urlWithToken;
                        return `${BACKEND_URL}${urlWithToken}`;
                      })()}
                      className="w-full h-full object-contain"
                      controls
                    />
                  )}
                  <button
                    onClick={() => {
                      setSelectedVideoId('');
                      setSelectedVideoData(null);
                    }}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 p-1.5 rounded-full text-white transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                    <p className="text-white text-sm truncate">{selectedVideoData.title}</p>
                  </div>
                </div>
              )
            ) : (
              // Image Mode Content
              selectedImage ? (
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden border-2 border-blue-500">
                  <img src={selectedImage.url} alt="Preview" className="w-full h-full object-contain" />
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 p-1 rounded-full text-white"
                  >
                    <Loader2 className="h-4 w-4 hidden" /> {/* Just to keep imports valid if heavily refactored */}
                    Remove
                  </button>
                </div>
              ) : (
                <div className="w-full h-40 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-500">
                  No image selected
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Caption & Tags */}
        <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-gray-300">{t('post.caption', 'Caption')}</Label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="What's this about?"
                className="bg-[#2a2a2b] border-gray-700 text-white"
                maxLength={500}
              />
            </div>

            <div>
              <Label className="text-gray-300">{t('post.tags', 'Tags (comma separated)')}</Label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="touchdown, highlights, gameday"
                className="bg-[#2a2a2b] border-gray-700 text-white"
              />
            </div>

            {mode === 'video' && activeEvent && (
              <div className="flex items-center justify-between p-4 bg-yellow-500/5 border border-yellow-500/10 rounded-xl space-x-4">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-500/10 p-2 rounded-lg">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white uppercase tracking-wider">Haftanın Oyunu Etkinliği</p>
                    <p className="text-[11px] text-gray-400 leading-tight">
                      {activeEvent?.user_has_entered
                        ? "Bu etkinliğe zaten bir video ile katıldın! Her etkinlik için bir katılım hakkın var."
                        : "Bu videoyu haftalık yarışmaya dahil et ve ödül kazanma şansı yakala!"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!activeEvent?.user_has_entered && (
                    <span className="text-[10px] font-black uppercase text-yellow-500/50 mr-1">{isEventEntry ? 'KATILIYOR' : 'KATIL'}</span>
                  )}
                  <Switch
                    checked={isEventEntry}
                    onCheckedChange={setIsEventEntry}
                    disabled={activeEvent?.user_has_entered}
                    className="data-[state=checked]:bg-yellow-500"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Button
          type="submit"
          disabled={(mode === 'video' && !selectedVideoId) || (mode === 'image' && !selectedImage) || submitting}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {submitting ? t('common.posting', 'Posting...') : "Post"}
        </Button>
      </form>
    </div>
  );
};

export default CreatePost;
